/**
 * Shared helpers of the batch feature (W2-11 batch-tools): selection, playhead and tempo reads from the services,
 * transactions with toasts, and the per-user tool defaults kept under `settings.ext` ('batch.*').
 */

import { buildTempoMap } from '../../core/doc/container';
import type { KeyId, LineId, NoteId } from '../../core/model/types';
import type { TxBuilder } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import type { Beat, TempoMap } from '../../core/time/types';
import type { Json, MessageRef } from '../../core/util/types';
import type { AppServices } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import type { DocStore, TxnMeta, TxnOutcome } from '../../state/types';
import { m } from './batch.messages';

export type Enabled = true | MessageRef;

export function ref(key: MessageKey | string, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

/** The open project's store, or the "no project" reason. */
export function requireStore(s: Pick<AppServices, 'store'>): DocStore | MessageRef {
  return s.store ?? ref(m.reasonNoProject);
}

const tempoCache = new WeakMap<DocStore, { readonly v: number; readonly map: TempoMap }>();

/** Phira-exact tempo map of the open document (the engine's when there is one). */
export function tempoOf(s: Pick<AppServices, 'store' | 'engine'>): TempoMap | null {
  const store = s.store;
  if (store === null) return null;
  if (s.engine !== null) return s.engine.tempo;
  const v = store.version('bpm');
  const hit = tempoCache.get(store);
  if (hit?.v === v) return hit.map;
  const map = buildTempoMap(store.doc, store.idx);
  tempoCache.set(store, { v, map });
  return map;
}

/** Current snap divisor (1..1920). */
export function snapDiv(s: Pick<AppServices, 'editor'>): number {
  const div = s.editor.getState().snap.div;
  return Number.isSafeInteger(div) && div >= 1 ? div : 4;
}

/** The transport's chart time (0 when the audio engine cannot tell). */
function chartTime(s: Pick<AppServices, 'audio'>): number {
  try {
    const t = s.audio.transport.chartTime();
    return Number.isFinite(t) ? t : 0;
  } catch {
    return 0;
  }
}

/** The playhead as an exact beat on the snap grid (beat 0 without a project). */
export function playheadBeat(s: Pick<AppServices, 'store' | 'engine' | 'audio' | 'editor'>): Beat {
  const tempo = tempoOf(s);
  if (tempo === null) return Beats.ZERO;
  const b = tempo.beatAt(Math.max(0, chartTime(s)));
  const snapped = Beats.snap(b, snapDiv(s));
  return Beats.cmp(snapped, Beats.ZERO) < 0 ? Beats.ZERO : snapped;
}

export interface SelectionIds {
  readonly notes: NoteId[];
  /** Keys selected as points. */
  readonly keys: KeyId[];
  /** Segments, by their start key. */
  readonly segments: KeyId[];
  /** Keys and segment start keys together (what a key-value tool works on). */
  readonly keyish: KeyId[];
}

/** The live selection, restricted to ids that still exist. */
export function selectionIds(s: Pick<AppServices, 'store' | 'editor'>): SelectionIds {
  const store = s.store;
  const sel = s.editor.getState().selection;
  if (store === null) return { notes: [], keys: [], segments: [], keyish: [] };
  const notes = [...sel.notes].filter((id) => store.doc.notes.has(id));
  const keys = [...sel.keys].filter((id) => store.doc.keys.has(id));
  const segments = [...sel.segments].filter((id) => store.doc.keys.has(id));
  const keyish = [...new Set([...keys, ...segments])];
  return { notes, keys, segments, keyish };
}

/** Line the tools work on: the active line, else the line of the first selected item. */
export function workingLine(s: Pick<AppServices, 'store' | 'editor'>): LineId | null {
  const store = s.store;
  if (store === null) return null;
  const st = s.editor.getState();
  if (st.activeLine !== null && store.doc.lines.has(st.activeLine)) return st.activeLine;
  const ids = selectionIds(s);
  const n = ids.notes[0];
  if (n !== undefined) return store.doc.notes.get(n)?.line ?? null;
  const k = ids.keyish[0];
  if (k !== undefined) {
    const key = store.doc.keys.get(k);
    const owner = key === undefined ? undefined : store.doc.tracks.get(key.track)?.owner;
    if (owner?.kind === 'line') return owner.line;
  }
  return null;
}

/** Shows a transaction's notes (at most three) as toasts. */
export function toastNotes(s: Pick<AppServices, 'ui'>, notes: readonly MessageRef[]): void {
  for (const n of notes.slice(0, 3)) s.ui.toast(n, { kind: 'info' });
}

/**
 * Runs one undoable transaction; a refusal becomes an error toast, notes become info toasts, and `done` (when
 * given) a success toast with an Undo link.
 */
export function transactWithToasts<R>(
  s: Pick<AppServices, 'store' | 'ui'>,
  meta: TxnMeta,
  build: (tx: TxBuilder) => R,
  done?: (value: R) => MessageRef | null,
): TxnOutcome<R> | null {
  const store = s.store;
  if (store === null) {
    s.ui.toast(ref(m.reasonNoProject), { kind: 'error' });
    return null;
  }
  const out = store.transact(meta, build);
  if (!out.ok) {
    s.ui.toast(out.reason, { kind: 'error' });
    return out;
  }
  const msg = done?.(out.value) ?? null;
  if (msg !== null && out.txn !== null) s.ui.toast(msg, { kind: 'success', undo: true });
  toastNotes(s, out.notes);
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Remembered tool settings (settings.ext 'batch.<tool>')
// ---------------------------------------------------------------------------------------------------------------

export const EXT = {
  bakeDensity: 'batch.bakeDensity',
  pathPerBeat: 'batch.pathPerBeat',
  distribute: 'batch.distribute',
  clone: 'batch.clone',
  fill: 'batch.fill',
  path: 'batch.path',
  flattenTolerance: 'batch.flattenTolerance',
  simplifyTolerance: 'batch.simplifyTolerance',
  fillHead: 'batch.fillHead',
} as const;

export const DEFAULT_BAKE_DENSITY = 8;
export const DEFAULT_PATH_PER_BEAT = 8;

/** A remembered number (finite, within [min, max]) or the default. */
export function extNumber(
  s: Pick<AppServices, 'settings'>,
  key: string,
  def: number,
  min: number,
  max: number,
): number {
  const v = s.settings.getExt(key);
  return typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : def;
}

/** A remembered object (plain JSON) or null. */
export function extObject(s: Pick<AppServices, 'settings'>, key: string): Readonly<Record<string, Json>> | null {
  const v = s.settings.getExt(key);
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Readonly<Record<string, Json>>) : null;
}

export function remember(s: Pick<AppServices, 'settings'>, key: string, value: Json): void {
  try {
    s.settings.setExt(key, value);
  } catch {
    // Preferences are a convenience; a failing settings store must not break the tool.
  }
}

/** Reads a field of a remembered object with a type guard. */
export function pick<T extends Json>(
  obj: Readonly<Record<string, Json>> | null,
  key: string,
  ok: (v: Json) => v is T,
  def: NoInfer<T>,
): T {
  const v = obj?.[key];
  return v !== undefined && ok(v) ? v : def;
}

export const isNum = (v: Json): v is number => typeof v === 'number' && Number.isFinite(v);
export const isBool = (v: Json): v is boolean => typeof v === 'boolean';
export const isStr = (v: Json): v is string => typeof v === 'string';
